import classNames from 'classnames';
import FuzzySearch from 'fuzzy-search';
import { useMemo, useState } from 'react';
import BasicPicker, { IBasicPickerProps, IBasicPickerValue } from '../BasicPicker/BasicPicker';
import './BasicSearchablePicker.scss';

export type BasicSearchablePickerProps<
  K extends string = string,
  T extends IBasicPickerValue<K> = IBasicPickerValue<K>,
> = Omit<IBasicPickerProps<K, T>, 'search' | 'onSearch'> & {
  searchType?: 'fuzzy' | 'substring';
  onEnter?: (searchValue: string) => void;
};

function BasicSearchablePicker<K extends string, T extends IBasicPickerValue<K>>({
  data,
  className,
  searchType = 'fuzzy', // default to 'fuzzy' so we don't mess up existing pickers using this as a base
  onEnter,
  ...pickerProps
}: BasicSearchablePickerProps<K, T>) {
  const [search, setSearch] = useState('');

  const fuzzy = useMemo(() => new FuzzySearch(data, ['name'], { caseSensitive: false }), [data]);

  const filteredValues = useMemo(() => {
    if (searchType === 'fuzzy') {
      return fuzzy.search(search);
    } else {
      return data.filter((item) => item.name.toLowerCase().includes(search.toLowerCase()));
    }
  }, [fuzzy, search, searchType, data]);

  const enterKeyPressed = () => {
    if (onEnter) {
      onEnter(search);
    }
  };

  const classes = classNames('basic-searchable-picker', className);

  return (
    <BasicPicker
      {...pickerProps}
      onEnterKeyDown={enterKeyPressed}
      onSearch={setSearch}
      searchValue={search}
      data={filteredValues}
      className={classes}
    />
  );
}

export default BasicSearchablePicker;
